import { useState, useEffect, useCallback, useRef } from "react";
import { Zap, Play, Square, Calculator } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * ⚡ Coin Combat — 60/40 (Over 3/Under 6) or 50/50 (Even/Odd): the payouts
 * where a capped doubling-style ladder truly recovers. ⚔️ 110% combat,
 * waits for winning numbers after a loss, STOPS at SL/TP, Survive-7 sizer.
 */
const MODES = [
  { key: "6040", label: "60/40", desc: "Over 3 / Under 6 · more wins" },
  { key: "5050", label: "50/50", desc: "Even / Odd · true doubling" },
];

const CoinCombat = ({ authorized, accountId, accessToken, onConnect }) => {
  const [st, setSt] = useState(null);
  const [stake, setStake] = useState("0.35");
  const [sl, setSl] = useState("30");
  const [tp, setTp] = useState("5");
  const [mode, setMode] = useState("5050");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const poll = useCallback(async () => {
    if (!accountId) return;
    try {
      const res = await api.get(`/coin-bot/status/${accountId}`);
      setSt(res.data);
    } catch { /* ignore */ }
  }, [accountId]);

  useEffect(() => {
    poll();
    timer.current = setInterval(poll, 4000);
    return () => clearInterval(timer.current);
  }, [poll]);

  const survive7 = async () => {
    try {
      const res = await api.get(`/coin-bot/survive7`, { params: { sl: Number(sl) || 30, mode } });
      setStake(String(res.data.base_stake));
      if (res.data.sl_needed_at_min_stake) {
        toast.warning(`⚠️ Even at the $0.35 minimum stake, surviving 7 straight losses needs ~$${res.data.sl_needed_at_min_stake} SL — raise your SL or accept fewer rungs`);
      } else {
        toast.success(`🧮 Survive-7: base $${res.data.base_stake} lets 7 straight losses fit inside your $${sl} SL`);
      }
    } catch { toast.error("Could not compute the Survive-7 stake"); }
  };

  const start = async () => {
    if (!authorized) { onConnect?.(); return; }
    setBusy(true);
    try {
      const res = await api.post("/coin-bot/start", {
        account_id: accountId, access_token: accessToken || "",
        stake: Number(stake) || 0.35, sl: Number(sl), tp: Number(tp),
        mode, auto_restart: false, disclaimer_accepted: true,
      });
      setSt(res.data);
      toast.success("⚡ Coin Combat ON — ⚔️ 110% combat armed, stops at SL/TP");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Coin Combat");
    }
    setBusy(false);
  };

  const stop = async () => {
    setBusy(true);
    try {
      await api.post(`/coin-bot/stop/${accountId}`);
      toast.success("⏹ Coin Combat off");
      poll();
    } catch { toast.error("Could not stop the bot"); }
    setBusy(false);
  };

  const running = !!st?.running;
  const net = Number(st?.net || 0);

  return (
    <div data-testid="coin-combat" className="rounded-lg border border-vts-line bg-vts-surface/40 p-3 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Zap className="w-4 h-4 text-yellow-300" />
        <p className="text-xs font-bold text-white">COIN COMBAT</p>
        <span className="text-[9px] font-mono text-vts-silver/50">recoverable-payout ladder · ⚔️ 110% combat · waits for winning numbers · STOPS at SL/TP</span>
        {running && (
          <span data-testid="coin-combat-live" className="ml-auto px-1.5 py-0.5 rounded bg-vts-profit/15 border border-vts-profit/40 text-[9px] font-bold font-mono text-vts-profit animate-pulse">
            ● ON · {st?.mode_label || st?.mode}
          </span>
        )}
      </div>

      {running ? (
        <>
          <div data-testid="coin-combat-stats" className="grid grid-cols-4 gap-2 text-center">
            {[
              { k: "net", label: "Net", val: `${net >= 0 ? "+" : "−"}$${Math.abs(net).toFixed(2)}`, c: net >= 0 ? "text-vts-profit" : "text-vts-loss" },
              { k: "wl", label: "W / L", val: `${st?.wins ?? 0}/${st?.losses ?? 0}`, c: "text-white" },
              { k: "side", label: "Side", val: st?.side || "—", c: "text-yellow-300" },
              { k: "phase", label: "Phase", val: st?.phase === "watching" ? `👀 ${st?.watch_proofs ?? 0}/2` : (st?.phase || "—"), c: "text-vts-silver/80" },
            ].map((x) => (
              <div key={x.k} data-testid={`coin-combat-${x.k}`} className="rounded bg-vts-panel border border-vts-line p-1.5">
                <p className={`text-[11px] font-bold font-mono ${x.c}`}>{x.val}</p>
                <p className="text-[8px] font-mono text-vts-silver/50">{x.label}</p>
              </div>
            ))}
          </div>
          {(st?.feed || []).length > 0 && (
            <div data-testid="coin-combat-feed" className="space-y-0.5 max-h-24 overflow-y-auto">
              {[...st.feed].reverse().slice(0, 6).map((f, i) => (
                <p key={i} className="text-[9px] font-mono text-vts-silver/55">
                  <span className="text-vts-silver/35">{String(f.ts).slice(11, 19)}</span> {f.text}
                </p>
              ))}
            </div>
          )}
          <button data-testid="coin-combat-stop-btn" onClick={stop} disabled={busy}
            className="w-full py-2 rounded-md bg-vts-loss/80 hover:bg-vts-loss text-white text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
            <Square className="w-3.5 h-3.5" /> STOP COIN COMBAT
          </button>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            {MODES.map((m) => (
              <button key={m.key} data-testid={`coin-combat-mode-${m.key}`} onClick={() => setMode(m.key)}
                className={`rounded border p-2 text-center transition ${mode === m.key
                  ? "border-yellow-400/60 bg-yellow-400/10 text-white"
                  : "border-vts-line bg-vts-panel text-vts-silver/60 hover:text-white"}`}>
                <p className="text-[12px] font-bold">{m.label}</p>
                <p className="text-[8.5px] font-mono opacity-70">{m.desc}</p>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: "coin-combat-stake", label: "Stake $", val: stake, set: setStake },
              { id: "coin-combat-sl", label: "🛑 Stop-loss $", val: sl, set: setSl },
              { id: "coin-combat-tp", label: "🎯 Take-profit $", val: tp, set: setTp },
            ].map((f) => (
              <label key={f.id} className="block">
                <span className="text-[8px] font-mono text-vts-silver/50">{f.label}</span>
                <input data-testid={f.id} type="number" min="0.35" step="0.01" value={f.val}
                  onChange={(e) => f.set(e.target.value)}
                  className="w-full mt-0.5 px-2 py-1.5 rounded bg-vts-panel border border-vts-line text-xs font-mono text-white" />
              </label>
            ))}
          </div>
          <button data-testid="coin-combat-survive7-btn" onClick={survive7}
            className="w-full py-1.5 rounded-md bg-vts-panel border border-vts-line text-vts-silver/80 hover:text-white text-[10px] font-mono font-bold flex items-center justify-center gap-1.5">
            <Calculator className="w-3 h-3" /> 🧮 Survive-7: size my stake so 7 straight losses fit inside my SL
          </button>
          <button data-testid="coin-combat-start-btn" onClick={start} disabled={busy}
            className="w-full py-2 rounded-md bg-yellow-500/85 hover:bg-yellow-500 text-black text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
            <Play className="w-3.5 h-3.5" /> START COIN COMBAT (24/7)
          </button>
          {st?.stopped_reason && (
            <p data-testid="coin-combat-stopped" className="text-[9px] font-mono text-amber-300/80 text-center">
              Last run ended: {String(st.stopped_reason).replace(/_/g, " ")}{st?.banked ? ` · banked $${Number(st.banked).toFixed(2)}` : ""}
            </p>
          )}
        </>
      )}

      {/* 📜 bot history — always visible */}
      {(st?.recent || []).length > 0 && (
        <div data-testid="coin-combat-history" className="space-y-1 max-h-40 overflow-y-auto">
          <p className="text-[9px] font-mono text-vts-silver/50 uppercase">Bot history</p>
          {st.recent.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px] font-mono rounded border border-vts-line/40 bg-vts-panel px-2 py-1">
              <span className="text-vts-silver/45">{String(r.settled_at || "").slice(11, 19)}</span>
              <span className="text-white">{r.contract_type?.replace("DIGIT", "")}{r.barrier != null ? ` ${r.barrier}` : ""} → {r.digit ?? "?"}</span>
              <span className="text-vts-silver/40">${Number(r.stake).toFixed(2)}</span>
              <span className={`ml-auto font-bold ${Number(r.profit) > 0 ? "text-vts-profit" : "text-vts-loss"}`}>
                {Number(r.profit) > 0 ? "+" : "−"}${Math.abs(Number(r.profit)).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CoinCombat;
